import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Upload, X } from 'lucide-react';
import { createPressRelease } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SimpleEditor from '@/components/SimpleEditor';
import { convertToHtml } from '@/lib/textUtils';
import { toast } from 'sonner';

const CreatePressRelease = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    company: '',
    location: '',
    date: new Date().toISOString().split('T')[0],
    featuredImage: '',
    content: '',
    excerpt: '',
    categories: '',
    tags: '',
    author: 'Ixoraly Media',
    status: 'published'
  });
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);
    
    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      // Auto-generate slug from title
      if (name === 'title') {
        updated.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }
      // Auto-generate excerpt from content
      if (name === 'content') {
        updated.excerpt = value.substring(0, 200);
      }
      return updated;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData(prev => ({ ...prev, featuredImage: base64String }));
        setImagePreview(base64String);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, featuredImage: '' }));
    setImagePreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        // Convert markdown-like content to HTML
        content: convertToHtml(formData.content),
        categories: formData.categories.split(',').map(c => c.trim()).filter(Boolean),
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      await createPressRelease(data);
      toast.success('Press release created successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error creating press release:', error);
      const message = error.response?.data?.detail || 'Failed to create press release';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="create-pr-page">
      <div className="bg-white rounded-lg shadow-sm p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Press Release</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <Input
              id="title"
              name="title"
              type="text"
              data-testid="create-title-input"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter press release title"
              className="text-base"
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-2">
              Slug * (URL-friendly identifier)
            </label>
            <Input
              id="slug"
              name="slug"
              type="text"
              data-testid="create-slug-input"
              value={formData.slug}
              onChange={handleChange}
              required
              placeholder="auto-generated-from-title"
              className="text-base"
            />
            <p className="text-xs text-gray-500 mt-1">Will be used in the URL: /newsroom/{formData.slug || 'your-slug'}</p>
          </div>

          {/* Company and Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company *
              </label>
              <Input
                id="company"
                name="company"
                type="text"
                data-testid="create-company-input"
                value={formData.company}
                onChange={handleChange}
                required
                placeholder="Company name"
                className="text-base"
              />
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <Input
                id="location"
                name="location"
                type="text"
                data-testid="create-location-input"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="City, State/Country"
                className="text-base"
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <Input
              id="date"
              name="date"
              type="date"
              data-testid="create-date-input"
              value={formData.date}
              onChange={handleChange}
              required
              className="text-base max-w-xs"
            />
          </div>

          {/* Featured Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image
            </label>
            
            {!imagePreview ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-navy-400 transition-colors">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="create-image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <span className="text-sm font-medium text-gray-700 mb-1">
                    Click to upload or drag and drop
                  </span>
                  <span className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                  </span>
                </label>
              </div>
            ) : (
              <div className="relative border-2 border-gray-200 rounded-lg p-4">
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors z-10"
                  data-testid="remove-image-button"
                >
                  <X size={16} />
                </button>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-64 mx-auto rounded-lg"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Image uploaded successfully
                </p>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <SimpleEditor
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter your press release content here...

Use the toolbar buttons to format your text:
- Bold, Italic for emphasis
- Headings for section titles
- Lists for bullet points
- Link button to insert hyperlinks

Double line breaks create new paragraphs."
              rows={16}
              dataTestId="create-content-input"
            />
          </div>

          {/* Categories and Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="categories" className="block text-sm font-medium text-gray-700 mb-2">
                Categories (comma-separated)
              </label>
              <Input
                id="categories"
                name="categories"
                type="text"
                data-testid="create-categories-input"
                value={formData.categories}
                onChange={handleChange}
                placeholder="Technology, Business, Finance"
                className="text-base"
              />
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <Input
                id="tags"
                name="tags"
                type="text"
                data-testid="create-tags-input"
                value={formData.tags}
                onChange={handleChange}
                placeholder="innovation, startup, investment"
                className="text-base"
              />
            </div>
          </div>

          {/* Author and Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-2">
                Author
              </label>
              <Input
                id="author"
                name="author"
                type="text"
                data-testid="create-author-input"
                value={formData.author}
                onChange={handleChange}
                placeholder="Author name"
                className="text-base"
              />
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                id="status"
                name="status"
                data-testid="create-status-select"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-transparent text-base"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              data-testid="create-pr-submit-button"
              disabled={loading}
              className="bg-navy-600 hover:bg-navy-700 text-white"
            >
              {loading ? 'Creating...' : (
                <span className="flex items-center space-x-2">
                  <Save size={18} />
                  <span>Create Press Release</span>
                </span>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="create-pr-cancel-button"
              onClick={() => navigate('/admin/dashboard')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePressRelease;